import { useState } from 'react';
import { Foundation } from '../types/Foundation';
import { isMockModeEnabled } from '../services/apiServiceFactory';
import { getMockFoundations } from '../mockData/mockFoundations';
import * as apiService from '../services/apiServiceFactory';

/**
 * Hook to fetch complete foundation details from either the API or mock data
 */
export function useFoundationDetails() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch complete foundation details by ID or name
   * In production mode, this always uses real data from the Supabase database
   */
  const fetchFoundationDetails = async (
    foundation: Foundation
  ): Promise<Foundation | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Only use mock data in development mode when specifically enabled
      if (process.env.NODE_ENV !== 'production' && isMockModeEnabled()) {
        console.log('Using mock data for foundation details');
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Find the complete foundation data in mock data
        const mockData = getMockFoundations();
        const foundationDetails = mockData.find(f => 
          f.id === foundation.id || f.name === foundation.name
        );
        
        setIsLoading(false);
        
        if (!foundationDetails) {
          setError('Foundation details not found in mock data');
          return null;
        }
        
        return foundationDetails;
      } else {
        // In production mode, ALWAYS use real data from Supabase
        let foundationDetails = null;
        
        try {
          // Try to get foundation by ID if available
          if (foundation.id) {
            foundationDetails = await apiService.getFoundationById(foundation.id);
          } 
          
          // If no ID or lookup fails, try searching by name
          if (!foundationDetails && foundation.name) {
            const searchResults = await apiService.searchFoundations(foundation.name, 1, 1);
            if (searchResults.foundations.length > 0) {
              const foundId = searchResults.foundations[0].id;
              if (foundId) {
                foundationDetails = await apiService.getFoundationById(foundId);
              }
            }
          }
          
          if (!foundationDetails) {
            // If we still don't have details, return the original foundation without modification
            // This ensures we never use mock data in production
            setIsLoading(false);
            console.warn(`Could not fetch complete details for foundation: ${foundation.name}`);
            return foundation;
          }
          
          // Log that we successfully got detailed data
          console.log('Successfully fetched detailed foundation data:', foundationDetails);
          
          setIsLoading(false);
          return foundationDetails;
        } catch (apiError) {
          console.error('API error fetching foundation details:', apiError);
          // On error, return the original foundation data without any mock additions
          setIsLoading(false);
          return foundation;
        }
      }
    } catch (err) {
      console.error('Unexpected error in useFoundationDetails:', err);
      setIsLoading(false);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      return foundation; // Return original data in case of error
    }
  };

  return {
    isLoading,
    error,
    fetchFoundationDetails
  };
}
